import React from 'react';
import {useParams} from 'react-router';

import {Card, CardContent} from '@design-system';

import useYoungPersonCategories from '../Queries/useYoungPersonCategories';
import {NUM_TRANSITION_TOPIC_LINKS} from '../Util/constants';
import {YOUNG_PERSON_CATEGORY_TOPIC_ICONS} from './transitionIcons';
import ShareButton from '../Shared/ShareResource/ShareButton';
import {findByRouteKey, useCanonicalRouteParams} from '../Util/routeIdentifier';
import {ResourceNotice, ResourceQueryState} from './LoadingCard';
import {RESOURCE_TITLE_CLASSES, ResourceBreadcrumbs, ResourceExternalLink} from './ProCategories';

const ProViewTopic = () => {
  const {category: categoryKey, topic: topicKey} = useParams();
  const {data: categories, error, refetch} = useYoungPersonCategories();
  // Each found by its slug, or by the numeric id of an address from before COM-477; a topic
  // is only ever found in its own category. One replace puts both slugs in the address,
  // whichever segments held an id, once both records are found.
  const category = findByRouteKey(categories, categoryKey);
  const topic = findByRouteKey(category?.topics, topicKey);
  useCanonicalRouteParams(
    {category: category?.slug, topic: topic?.slug},
    {resource: 'young-person-transition-topic'}
  );
  const subtopics = topic?.subtopics ? topic.subtopics.split('~') : [];
  const topicIcon = topic ? YOUNG_PERSON_CATEGORY_TOPIC_ICONS[topic.key] : undefined;

  let topicLinks = [];
  if (topic) {
    for (let i = 0; i < NUM_TRANSITION_TOPIC_LINKS; i++) {
      const linkNumber = i + 1;
      const link = topic[`link_${linkNumber}`];
      const title = topic[`link_${linkNumber}_title`];
      if (link) {
        topicLinks.push({link, title});
      }
    }
  }

  return (
    <div data-testid="pro-view-topic">
      <ResourceBreadcrumbs
        items={[
          {label: 'Home', to: '/dashboard'},
          {label: 'Young People', to: '/young-person-categories'},
          ...(category
            ? [
                {
                  label: category.name,
                  to: `/young-person-categories/${encodeURIComponent(category.slug)}`,
                },
              ]
            : []),
          ...(topic ? [{label: topic.name}] : []),
        ]}
      />
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {topicIcon ? (
            <img src={topicIcon} width={42} height={42} alt="" className="size-[42px] shrink-0" />
          ) : null}
          <h1 className={RESOURCE_TITLE_CLASSES}>{topic?.name ?? 'Young People'}</h1>
        </div>
        {topic ? (
          <ShareButton
            resourceType="topic"
            resourceId={topic.id}
            resourceSlug={topic.slug}
            resourceName={topic.name}
          />
        ) : null}
      </div>
      <ResourceQueryState
        data={categories}
        error={error}
        refetch={refetch}
        label="young person resources">
        {topic ? (
          <Card>
            <CardContent className="p-6">
              <p className="m-0 break-words">{topic.description}</p>
              {subtopics.length ? (
                <ul data-testid="pro-topic-subtopics" className="mt-4 list-disc space-y-1 pl-6">
                  {subtopics.map((subtopic, index) => (
                    <li key={`subtopic-${index}`} className="break-words">
                      {subtopic}
                    </li>
                  ))}
                </ul>
              ) : null}
              <h2 className="mt-6 mb-2 text-lg font-semibold">Helpful Links</h2>
              <ul data-testid="pro-topic-links" className="m-0 flex list-none flex-col gap-1 p-0">
                {topicLinks.map((link, index) => (
                  <li key={`link-${index}`}>
                    <ResourceExternalLink href={link.link}>{link.title}</ResourceExternalLink>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ) : (
          <ResourceNotice>Topic not found.</ResourceNotice>
        )}
      </ResourceQueryState>
    </div>
  );
};

export default ProViewTopic;
